// CSR-822: sectioned list view for the Insurance Companies tab. Three
// hard-coded sections in PRD order: Parent Companies → Insurance
// Carriers → MGAs. Each section sorts alphabetically by name and has
// its own empty-state copy + count badge.
//
// The "+ Add Insurance Company" button currently surfaces the parent's
// onAddCompany callback (CSR-823 wires the actual modal). The edit
// pencil on each card does the same -- both routes converge on the
// same modal in CSR-823.

import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus } from "lucide-react";
import type { InsuranceCompany } from "@shared/schema";
import { useInsuranceCompanies } from "@/lib/api";
import { InsuranceCompanyCard } from "./InsuranceCompanyCard";

function byName(a: InsuranceCompany, b: InsuranceCompany): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

interface SectionProps {
  title: string;
  emptyCopy: string;
  companies: InsuranceCompany[];
  childCountByParent?: Map<string, number>;
  parentNameById?: Map<string, string>;
  onEdit: (company: InsuranceCompany) => void;
  testid: string;
  hideWhenEmpty?: boolean;
}

function Section({
  title,
  emptyCopy,
  companies,
  childCountByParent,
  parentNameById,
  onEdit,
  testid,
  hideWhenEmpty,
}: SectionProps) {
  const sorted = useMemo(() => [...companies].sort(byName), [companies]);

  // During an active search, a section with no matches is hidden entirely
  // rather than showing its "No X yet." placeholder (CSR-1654).
  if (hideWhenEmpty && sorted.length === 0) return null;

  return (
    <section className="flex flex-col gap-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
        <Badge variant="secondary" data-testid={`${testid}-count`}>
          {companies.length}
        </Badge>
      </div>

      {sorted.length === 0 ? (
        <p
          className="text-sm text-muted-foreground italic"
          data-testid={`${testid}-empty`}
        >
          {emptyCopy}
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {sorted.map((company) => (
            <InsuranceCompanyCard
              key={company.id}
              company={company}
              childCount={childCountByParent?.get(company.id)}
              parentName={
                company.parentCompanyId
                  ? parentNameById?.get(company.parentCompanyId)
                  : undefined
              }
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SectionSkeleton({ testid }: { testid: string }) {
  return (
    <section className="flex flex-col gap-3" data-testid={`${testid}-skeleton`}>
      <Skeleton className="h-4 w-40" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-32 w-full rounded-md" />
        ))}
      </div>
    </section>
  );
}

interface InsuranceCompaniesListProps {
  // Search query is owned by the parent Companies page so the field can live
  // on the tab row (CSR-1654); this component filters against it.
  search: string;
  onAddCompany: () => void;
  onEditCompany: (company: InsuranceCompany) => void;
}

export function InsuranceCompaniesList({
  search,
  onAddCompany,
  onEditCompany,
}: InsuranceCompaniesListProps) {
  const { data, isLoading, error } = useInsuranceCompanies();

  // Parent → child-count map. Both carriers and MGAs can be children of
  // a parent, per CSR-816 schema.
  const childCountByParent = useMemo(() => {
    const map = new Map<string, number>();
    if (!data) return map;
    for (const child of [...data.insuranceCarriers, ...data.mgas]) {
      if (child.parentCompanyId) {
        map.set(
          child.parentCompanyId,
          (map.get(child.parentCompanyId) ?? 0) + 1,
        );
      }
    }
    return map;
  }, [data]);

  // Parent id → name, used by child cards to render the Parent line.
  const parentNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    for (const parent of data.parentCompanies) {
      map.set(parent.id, parent.name);
    }
    return map;
  }, [data]);

  // Client-side filter across all three categories (CSR-1654). The list is
  // already fully loaded and pre-bucketed, so we filter in memory (no
  // debounce needed at this size) and restore the full page when cleared.
  const q = search.trim().toLowerCase();
  const isSearching = q.length > 0;

  const filtered = useMemo(() => {
    if (!data) return null;
    if (!isSearching) return data;
    const matches = (company: InsuranceCompany) =>
      company.name.toLowerCase().includes(q) ||
      (company.naic?.toLowerCase().includes(q) ?? false);
    return {
      parentCompanies: data.parentCompanies.filter(matches),
      insuranceCarriers: data.insuranceCarriers.filter(matches),
      mgas: data.mgas.filter(matches),
    };
  }, [data, q, isSearching]);

  const noResults =
    isSearching &&
    filtered !== null &&
    filtered.parentCompanies.length === 0 &&
    filtered.insuranceCarriers.length === 0 &&
    filtered.mgas.length === 0;

  return (
    <div className="flex flex-col gap-6" data-testid="insurance-companies-list">
      <div className="flex items-center justify-end">
        <Button
          onClick={onAddCompany}
          data-testid="button-add-insurance-company"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Insurance Company
        </Button>
      </div>

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
          data-testid="insurance-companies-error"
        >
          Failed to load insurance companies. Please try again.
        </div>
      ) : isLoading || !data || !filtered ? (
        <>
          <SectionSkeleton testid="section-parent-companies" />
          <SectionSkeleton testid="section-insurance-carriers" />
          <SectionSkeleton testid="section-mgas" />
        </>
      ) : noResults ? (
        <p
          className="text-sm text-muted-foreground italic"
          data-testid="insurance-companies-no-results"
        >
          No companies found.
        </p>
      ) : (
        <>
          <Section
            title="Parent Companies"
            emptyCopy="No parent companies yet."
            companies={filtered.parentCompanies}
            childCountByParent={childCountByParent}
            parentNameById={parentNameById}
            onEdit={onEditCompany}
            testid="section-parent-companies"
            hideWhenEmpty={isSearching}
          />
          <Section
            title="Insurance Carriers"
            emptyCopy="No insurance carriers yet."
            companies={filtered.insuranceCarriers}
            parentNameById={parentNameById}
            onEdit={onEditCompany}
            testid="section-insurance-carriers"
            hideWhenEmpty={isSearching}
          />
          <Section
            title="MGAs"
            emptyCopy="No MGAs yet."
            companies={filtered.mgas}
            parentNameById={parentNameById}
            onEdit={onEditCompany}
            testid="section-mgas"
            hideWhenEmpty={isSearching}
          />
        </>
      )}
    </div>
  );
}

// Total count helper used by the tab badge in Companies.tsx so we don't
// have to fetch the list twice.
export function getTotalCount(
  data: ReturnType<typeof useInsuranceCompanies>["data"],
): number {
  if (!data) return 0;
  return (
    data.parentCompanies.length +
    data.insuranceCarriers.length +
    data.mgas.length
  );
}
